import { useState } from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Account } from '@mls-wallet/shared-types';
import { formatAddress } from '@/pages/policies/utils';

interface AccountSelectorProps {
  accounts: Account[];
  selectedAccounts: Account[];
  onSelectionChange: (accounts: Account[]) => void;
  placeholder?: string;
  className?: string;
  size?: 'default' | 'sm' | 'lg';
}

export function AccountSelector({
  accounts,
  selectedAccounts,
  onSelectionChange,
  placeholder = 'Select accounts',
  className,
  size = 'default',
}: AccountSelectorProps) {
  // Disable if no accounts are available
  const isDisabled = accounts.length === 0;
  const [open, setOpen] = useState(false);

  const handleAccountToggle = (account: Account) => {
    const isSelected = selectedAccounts.some((a) => a.id === account.id);
    if (isSelected) {
      onSelectionChange(selectedAccounts.filter((a) => a.id !== account.id));
    } else {
      onSelectionChange([...selectedAccounts, account]);
    }
  };

  const handleAccountRemove = (accountId: string) => {
    onSelectionChange(selectedAccounts.filter((a) => a.id !== accountId));
  };

  const getDisplayText = () => {
    if (selectedAccounts.length === 0) return 'Any account';
    return `${selectedAccounts.length} account${selectedAccounts.length > 1 ? 's' : ''} selected`;
  };

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            size={size}
            disabled={isDisabled}
            className={cn(
              'w-full justify-between border-input text-base',
              className
            )}
          >
            {getDisplayText()}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0" align="start">
          <Command>
            <CommandInput
              placeholder={`Search ${placeholder.toLowerCase()}...`}
            />
            <CommandEmpty>No accounts found.</CommandEmpty>
            <CommandGroup>
              {accounts.map((account) => {
                const isSelected = selectedAccounts.some(
                  (a) => a.id === account.id
                );
                return (
                  <CommandItem
                    key={account.id}
                    onSelect={() => handleAccountToggle(account)}
                  >
                    <div className="flex items-center">
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          isSelected ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      <div className="flex flex-col">
                        <span>{account.name}</span>
                        <span className="text-xs text-muted-foreground font-mono">
                          {formatAddress(account.address)}
                        </span>
                      </div>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>

      {selectedAccounts.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedAccounts.map((account) => (
            <Badge
              key={account.id}
              variant="secondary"
              className="flex items-center gap-1"
            >
              <span>{account.name}</span>
              <X
                className="ml-1 h-3 w-3 cursor-pointer"
                onClick={() => handleAccountRemove(account.id)}
              />
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
